import React from 'react';
import classNames from 'classnames';
import { omit } from 'lodash';
import warning from 'warning';
import vdlPrefix from '../util/vdlPrefix';
import { Validations } from './Validations';
import { FormatHelper } from '@adp-wfn/mdf-core';
import { resolveAriaProperty } from '@synerg/vdl-react-components';

let didWarnValueDefaultValue = false;

export interface ITextboxValidationObject {
  valid: boolean;
  message: string;
}

export interface ITextboxProps {
  // Accessibility: links component to the corresponding popover message.
  ariaDescribedby?: string;
  'aria-describedby'?: string;
  ariaInvalid?: any;
  'aria-invalid'?: any;
  ariaLabel?: string;
  'aria-label'?: string;
  ariaRequired?: boolean;
  'aria-required'?: boolean;
  autoComplete?: string;
  autoFocus?: boolean | 'selectAll';
  className?: string;
  defaultValue?: string;
  disabled?: boolean;
  errorId?: string;
  immediate?: boolean;
  limitRegExp?: RegExp;
  maxLength?: number;
  minLength?: number;
  name?: string;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  selectAllOnFocus?: boolean;
  tabIndex?: number;
  takeFocus?: boolean;
  type?: string;
  validationObject?: ITextboxValidationObject;
  value?: string;
  onBlur?: (value?) => void;
  onChange?: (value: string) => void;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  onFocus?: () => void;
}

export interface ITextboxState {
  hasFocus?: boolean;
  initialValue?: string;
  isTouched?: boolean;
  takeFocus?: boolean;
  value?: string;
}

// This array exists to make it possible to use lodash functions to filter properties.
// It's not possible to extract keys from a TypeScript interface, but we can make sure
// the array contains all the necessary keys.
const textboxPropsKeys = [
  'ariaDescribedby',
  'aria-describedby',
  'ariaInvalid',
  'aria-invalid',
  'ariaLabel',
  'aria-label',
  'ariaRequired',
  'aria-required',
  'autoComplete',
  'autoFocus',
  'className',
  'defaultValue',
  'disabled',
  'errorId',
  'immediate',
  'limitRegExp',
  'maxLength',
  'minLength',
  'name',
  'placeholder',
  'readOnly',
  'required',
  'selectAllOnFocus',
  'tabIndex',
  'takeFocus',
  'type',
  'validationObject',
  'value',
  'onBlur',
  'onChange',
  'onClick',
  'onFocus'
];

export class Textbox extends React.PureComponent<ITextboxProps, ITextboxState> {
  static defaultProps: ITextboxProps = {
    type: 'text',
    autoFocus: false
  };

  state: ITextboxState = {
    hasFocus: false,
    isTouched: false,
    takeFocus: false
  };

  _input: HTMLInputElement;

  componentDidUpdate() {
    if (this.state.takeFocus) {
      this.takeFocus();
    }
  }

  static getDerivedStateFromProps(nextProps: ITextboxProps, currentState: ITextboxState) {
    const nextState: ITextboxState = {};

    if ((nextProps.value !== undefined) && (nextProps.defaultValue !== undefined) && !didWarnValueDefaultValue) {
      warning(false,
        'Input elements must be either controlled or uncontrolled ' +
        '(specify either the value prop, or the defaultValue prop, but not ' +
        'both). Decide between using a controlled or uncontrolled input ' +
        'element and remove one of these props. More info: ' +
        'https://fb.me/react-controlled-components'
      );

      didWarnValueDefaultValue = true;
    }

    if (currentState.initialValue === undefined) {
      nextState.initialValue = nextProps.value;
      nextState.value = nextProps.value;
    }

    if (nextProps.immediate) {
      // If nextProps.immediate is true, then onChange fires on every change, so the new value should reflect the current state.
      if (nextProps.value !== currentState.value) {
        nextState.value = nextProps.value;
      }
    }
    else {
      // If nextProps.immediate is not true, then onChange fires only on blur, so nextProps.value will not match the current state,
      // so we need to ignore the reflected value, unless it is different from the initial value, which shows that the app updated
      // the value property instead of the user.
      if (nextProps.value !== currentState.initialValue || !currentState.hasFocus) {
        // If the app somehow changed the value, then we need to reflect that.
        nextState.initialValue = nextProps.value;
        nextState.value = nextProps.value;
      }
      else {
        // Otherwise, make sure the current state stays the current state.
        nextState.value = currentState.value;
      }
    }

    if (nextProps.takeFocus !== undefined && nextProps.takeFocus !== currentState.takeFocus) {
      nextState.takeFocus = nextProps.takeFocus;
    }

    if (Object.keys(nextState).length > 0) {
      return nextState;
    }
    else {
      return null;
    }
  }

  getValue = () => this.state.value;

  hasFocus = () => this.state.hasFocus;

  isTouched = () => this.state.isTouched;

  private handleChange = (event) => {
    // There are several React bugs in IE,
    // where the `input`'s `onChange` event is
    // fired even when the value didn't change.
    // https://github.com/facebook/react/issues/2185
    // https://github.com/facebook/react/issues/3377

    const newValue = event.target.value;
    let isAllowedValue = true;

    if (this.props.limitRegExp) {
      isAllowedValue = (new RegExp(this.props.limitRegExp)).test(newValue) || newValue === '';
    }

    if (isAllowedValue && newValue !== this.state.value) {
      this.setState({
        value: newValue,
        isTouched: true
      });

      if (this.props.onChange && this.props.immediate) {
        this.props.onChange(newValue);
      }
    }
  };

  blur = () => {
    if (this._input) {
      this._input.blur();
    }
  };

  private handleBlur = () => {
    this.setState({
      hasFocus: false,
      isTouched: true
    });

    if (this.props.immediate && this.props.onBlur) {
      this.props.onBlur(this.state.value);
    }
    else if (this.props.onChange) {
      this.props.onChange(this.state.value);
    }
  };

  private handleFocus = () => {
    this.setState(
      {
        hasFocus: true,
        isTouched: true
      },
      () => {
        if (this.props.selectAllOnFocus) {
          this.takeFocus();
        }

        if (this.props.onFocus) {
          this.props.onFocus();
        }
      }
    );
  };

  private onInputMounted = (input) => {
    this._input = input;

    if (!input) {
      return;
    }

    if (this.state.takeFocus) {
      input.focus();
      input.setSelectionRange(0, input.value.length);
    }

    if (this.props.autoFocus === 'selectAll') {
      try {
        input.setSelectionRange(0, input.value.length);
      }
      catch (ex) {
        // ignore
      }
    }
  };

  private handleClick = (event) => {
    if (this.props.onClick) {
      this.props.onClick(event);
    }
  };

  private takeFocus = () => {
    if (this._input) {
      this._input.focus();
      this._input.setSelectionRange(0, this._input.value.length);
    }
  };

  renderAriaLabel = (value) => {
    let label;
    const regex = /-[1-9][0-9]*/g;

    // Convert number to string
    const stringVal = (typeof value !== 'string') ? value.toString() : value;
    const ariaLabel = resolveAriaProperty('Textbox', 'aria-label', 'ariaLabel', this.props);

    // The aria-label may show a negative number accessibility message and/or optional ariaLabel props.
    if (stringVal?.match(regex) && ariaLabel) {
      label = FormatHelper.formatMessage('@@OneOrMoreNegativeNumbers') + ' ' + ariaLabel;
    }
    else if (stringVal?.match(regex) && !ariaLabel) {
      label = FormatHelper.formatMessage('@@OneOrMoreNegativeNumbers');
    }
    else if (ariaLabel && !stringVal?.match(regex)) {
      label = ariaLabel;
    }

    return label;
  };

  render() {
    const {
      errorId,
      className,
      disabled,
      readOnly,
      required,
      autoFocus,
      placeholder,
      minLength,
      maxLength,
      type,
      name,
      autoComplete
    } = this.props;
    const ariaInvalid = resolveAriaProperty('Textbox', 'aria-invalid', 'ariaInvalid', this.props);
    const ariaRequired = resolveAriaProperty('Textbox', 'aria-required', 'ariaRequired', this.props);

    // Convert null to an empty string for display purposes.
    const value = Validations.isExisty(this.state.value) ? this.state.value : '';

    const textBoxClasses = {
      [vdlPrefix('textbox')]: true,
      [vdlPrefix('textbox--touched')]: this.state.isTouched
    };

    const customProps = omit(this.props, textboxPropsKeys);
    const ariaDescribedby = resolveAriaProperty('Textbox', 'aria-describedby', 'ariaDescribedby', this.props);

    return (
      <input
        {...customProps}
        aria-describedby={ariaDescribedby || errorId}
        aria-invalid={ariaInvalid}
        aria-label={this.renderAriaLabel(value)}
        aria-required={ariaRequired || required}
        autoComplete={autoComplete}
        autoFocus={!!autoFocus}
        className={classNames(className, textBoxClasses)}
        disabled={disabled}
        maxLength={maxLength}
        minLength={minLength}
        name={name}
        placeholder={placeholder}
        readOnly={readOnly}
        ref={this.onInputMounted}
        tabIndex={this.props.tabIndex}
        type={type}
        value={value}
        onBlur={this.handleBlur}
        onChange={this.handleChange}
        onClick={this.handleClick}
        onFocus={this.handleFocus}
      />
    );
  }
}
